<app-uni-dialog [parent]="this" [contentTemplate]="contentTemplate" [footerTemplate]="footerTemplate"></app-uni-dialog>

<ng-template #contentTemplate>
  <!-- Something has to be here from the first frame. Gating the whole dialog on isPageReady left an
       empty shell for as long as the queue took to read, which reads as the page flashing rather
       than as a dialog opening, and invites a second click on a dialog that is already open. -->
  <div *ngIf="!isPageReady" class="flex items-center gap-2 p-4 text-sm">
    <i class="pi pi-spin pi-spinner"></i>
    <span>The system reads the feed sizes.</span>
  </div>

  <div *ngIf="isPageReady" class="flex flex-col h-full">

    <div class="px-3 pt-3 pb-2">
      <div class="qb-detail-card qb-primary-tint p-3">
        <table class="qb-info-table w-full">
          <tr>
            <td class="label">Manufacturer</td>
            <td class="value font-semibold">{{ params.manufacturerName }}</td>
          </tr>
          <tr *ngIf="isFocused && params.focusFeedSizes?.length && params.focusLabel">
            <td class="label">Product type</td>
            <td class="value font-semibold">{{ params.focusLabel }}</td>
          </tr>
          <tr *ngIf="isFocused && !params.focusFeedSizes?.length">
            <td class="label">Uniformax size list</td>
            <td class="value font-semibold">{{ params.focusFeatureName || 'The size list of this style' }}</td>
          </tr>
          <tr>
            <td class="label">Feed sizes to answer</td>
            <td class="value font-semibold">
              {{ shownQuestions }}
              <span class="font-normal">&mdash; {{ shownParts }} feed part(s)</span>
              <!-- A part count covers every style on the list, because that is how far an answer
                   reaches. Said, so it does not read as this product type's parts. -->
              <span *ngIf="isFocused" class="font-normal" style="color: var(--p-text-muted-color)">
                on every style that uses the same Uniformax size list
              </span>
            </td>
          </tr>
          <tr>
            <td class="label">With a suggestion</td>
            <td class="value">{{ shownWithProposal }} of {{ shownQuestions }}</td>
          </tr>
          <tr *ngIf="answeredCount > 0">
            <td class="label">Answered just now</td>
            <td class="value font-semibold">
              {{ answeredCount }} feed size(s), {{ answeredParts }} feed part(s)
            </td>
          </tr>
        </table>
      </div>
    </div>

    <div class="px-3 pb-2 text-sm">
      The feed names sizes its own way. Say what each feed size means in Uniformax. You do this one
      time, and the answer applies to every style that uses the same Uniformax size list. This does
      not change any product.
    </div>

    <!-- Opened from a style's health panel, which already knows which sizes it is waiting on. The
         Styles column still shows how far each answer reaches, so narrowing the view does not hide
         that the answer travels beyond this style. -->
    <div *ngIf="isFocused && !isFinished" class="px-3 pb-2 flex items-center gap-2 text-sm">
      <i class="pi pi-filter" style="color: var(--p-primary-color)"></i>
      <!-- Reopened: these sizes have an answer already and the operator came to change it. Said
           plainly, because the rest of this screen is about sizes with no answer at all. -->
      <span *ngIf="isReopening">
        Showing {{ focusedCandidates.length }} feed size(s) that have an answer already. Select a
        feed size to change what it means. Each answer also applies to the other styles that use
        the same Uniformax size list.
      </span>
      <span *ngIf="!isReopening && params.focusFeedSizes?.length">
        Showing the {{ focusedCandidates.length }} feed size(s)
        <ng-container *ngIf="params.focusLabel">{{ params.focusLabel }}</ng-container>
        needs. Each answer also applies to the other styles that use the same Uniformax size list.
      </span>
      <!-- Narrowed to a size list only. Name the list, because that is what the answers apply to
           and the style is merely how the operator got here. -->
      <span *ngIf="!isReopening && !params.focusFeedSizes?.length">
        Showing the {{ focusedCandidates.length }} feed size(s) on
        <b>{{ params.focusFeatureName || 'this Uniformax size list' }}</b
        ><ng-container *ngIf="params.focusLabel">, the Uniformax size list {{ params.focusLabel }} uses</ng-container>.
        Each answer also applies to the other styles that use the same Uniformax size list.
      </span>
      <button
        *ngIf="canShowEverything"
        pButton
        type="button"
        class="p-button-text p-button-sm ml-auto"
        (click)="showEverything()"
      >
        <span>Show all {{ remainingQuestions }}</span>
      </button>
    </div>

    <!-- The caller's sizes are all answered. Said in words, with nothing more to press: the rest of
         the queue belongs to other styles, and offering it here is what took the context away. -->
    <div *ngIf="isFinished" class="mx-3 mb-2 p-3 qb-detail-card flex items-center gap-3">
      <i class="pi pi-check-circle text-xl" style="color: var(--p-green-600, #16a34a)"></i>
      <span *ngIf="params.focusFeedSizes?.length">
        Every feed size {{ params.focusLabel || 'this style' }} needs has an answer. Close this dialog.
      </span>
      <span *ngIf="!params.focusFeedSizes?.length">
        Every feed size on <b>{{ params.focusFeatureName || 'this Uniformax size list' }}</b> has an
        answer. Close this dialog.
      </span>
      <button
        *ngIf="canShowEverything"
        pButton
        type="button"
        class="p-button-text p-button-sm ml-auto"
        (click)="showEverything()"
      >
        <span>Show all {{ remainingQuestions }}</span>
      </button>
    </div>

    <!-- Every suggestion at once. These are the ones the system can already justify - an exact
         name match, or the rule named in the Why column - so agreeing with them one at a time is
         a hundred presses to confirm something already worked out. Scoped to what is on screen,
         so a narrowed view answers only what it shows. -->
    <div *ngIf="visibleWithProposal.length" class="px-3 pb-2 flex items-center gap-3">
      <button
        pButton
        type="button"
        class="p-button-raised p-button-sm"
        icon="pi pi-check-circle"
        [loading]="isApplying"
        [disabled]="!canAcceptAll"
        (click)="onAcceptAllSuggested()"
      >
        <span>Accept All {{ visibleWithProposal.length }} Suggested</span>
      </button>
      <span class="text-sm" style="color: var(--p-text-muted-color)">
        Records every feed size that already has a suggestion. The ones that need your judgement are
        left alone.
      </span>
    </div>

    <!-- Feed sizes the Uniformax size list has no name for, where the list already writes sizes of
         that shape the feed's way. Adds them word for word, then matches them. -->
    <div *ngIf="addableSizes.length" class="px-3 pb-2 flex items-center gap-3">
      <button
        pButton
        type="button"
        class="p-button-raised p-button-sm"
        icon="pi pi-plus"
        [loading]="isApplying"
        [disabled]="isApplying"
        (click)="onAddToSizeList()"
      >
        <span>Add {{ addableSizes.length }} Feed Size(s) to the Uniformax Size List</span>
      </button>
      <span class="text-sm" style="color: var(--p-text-muted-color)">
        The Uniformax size list has no size with these names. The system adds each one exactly as
        the feed writes it. Then it matches them.
      </span>
    </div>

    <!-- Hidden rather than removed, so the grid and its api survive for Show all. -->
    <div class="flex-1 min-h-0 px-3" [class.hidden]="isFinished">
      <app-ag-grid [gridOptions]="gridOptions" class="h-full"></app-ag-grid>
    </div>

    <!-- The answer panel. Appears on selection so the grid keeps its height while scanning.
         A two-row grid, not uni-field: uni-field pads its own label, so the feed size and the
         arrow never sat on the dropdown's center line. Labels share row one, controls row two. -->
    <div *ngIf="selected" class="mx-3 my-3 p-3 qb-detail-card">
      <div class="flex items-center gap-6 flex-wrap">
        <div class="grid items-center gap-x-3 gap-y-1" style="grid-template-columns: auto auto auto auto">
          <label class="text-sm" style="color: var(--p-text-muted-color)">The feed size is</label>
          <span></span>
          <label for="sizeMappingChoice" class="text-sm" style="color: var(--p-text-muted-color)">
            In Uniformax this means
          </label>
          <span></span>

          <div class="font-semibold text-lg whitespace-nowrap">
            {{ selected.FeedSize }}
            <span *ngIf="selected.ApparelStyle" class="font-normal text-base">
              ({{ selected.ApparelStyle }})
            </span>
          </div>
          <i class="pi pi-arrow-right" style="color: var(--p-text-muted-color)"></i>
          <!-- appendTo body: the answer panel sits at the foot of the dialog, so an inline
               overlay is clipped by the dialog's own overflow and the list is unreadable. -->
          <p-dropdown [options]="choices" [(ngModel)]="chosenId" optionValue="id" optionLabel="label"
                      inputId="sizeMappingChoice" [filter]="true" filterBy="label"
                      placeholder="Select a Uniformax size..." appendTo="body"
                      [style]="{ 'min-width': '260px' }" [disabled]="isApplying" />
          <button pButton type="button" class="p-button-raised p-button-sm" icon="pi pi-check"
                  [loading]="isApplying" [disabled]="!canApply" (click)="onApply()">
            <span>Record This Size</span>
          </button>
        </div>

        <div class="ml-auto text-sm" style="color: var(--p-text-muted-color)">
          <ng-container *ngIf="selected.ProposedRule">
            Suggested from the <b>{{ selected.ProposedRule }}</b> rule.
          </ng-container>
          <ng-container *ngIf="!selected.ProposedRule">
            No suggestion &mdash; this one needs your judgement.
          </ng-container>
          <div>Affects {{ selected.PartCount }} feed part(s) on {{ selected.StyleCount }} style(s).</div>
        </div>
      </div>

      <!-- Only a reopened row has one. The operator is replacing an answer, so the one they are
           replacing has to be on screen beside the one they pick. -->
      <div *ngIf="selected.CurrentChoiceValue" class="mt-2 text-sm">
        The feed size is matched today to the Uniformax size <b>{{ selected.CurrentChoiceValue }}</b>.
      </div>

      <div class="mt-2 text-sm" style="color: var(--p-text-muted-color)">
        Seen on {{ selected.SampleStyles.join(', ') }}<span *ngIf="selected.StyleCount > selected.SampleStyles.length">
          and {{ selected.StyleCount - selected.SampleStyles.length }} more</span>.
      </div>
    </div>

    <div *ngIf="errorMessage" class="px-3 pb-2 text-sm" style="color: var(--p-red-500, #ef4444)">
      {{ errorMessage }}
    </div>
  </div>
</ng-template>

<ng-template #footerTemplate>
  <div class="flex flex-row gap-2">
    <button pButton type="button" class="p-button-raised" (click)="onClose()">
      <span>Close</span>
    </button>
  </div>
</ng-template>
